import { ReactNode, useEffect, useState } from 'react';
import { Link } from '@anytag/router';
import { useAuthData } from '@anytag/auth';
import { Icon } from '@anytag/shared-atoms';
import { useLocationHistory, useQueryHelper } from '@anytag/shared-hooks';
import { cn } from '@anytag/shared-utils';

export interface HeaderProps {
  className?: string;
  children?: ReactNode;
}

export const Header = ({ className, children }: HeaderProps) => {
  const {
    auth: { href, title, alwaysBackToParent, backInHistory, search }
  } = useAuthData();
  const { lastLocation } = useLocationHistory();
  const [historyOnMount, setHistoryOnMount] = useState<typeof lastLocation>(null);
  const { pathname } = useQueryHelper();
  const backToParentUrl = href;

  useEffect(() => {
    if (lastLocation) {
      setHistoryOnMount(lastLocation);
    }
  }, []);

  /**
   * after we redirecting from FB or other external links we don't want user to go there or back to external domains
   * if lastLocation contains any "/redirect" means we just came from sign-up process and should use "backToParentUrl"
   * or lastLocation?.pathname && lastLocation?.search might be the same as current one - we should use "backToParentUrl" as well
   */
  const cannotGoBack = historyOnMount?.pathname?.includes('/redirect') || pathname === historyOnMount?.pathname;

  const goBackUrl =
    alwaysBackToParent && backToParentUrl // if we have alwaysBackToParent flag, we should always go back to parent url
      ? backToParentUrl
      : cannotGoBack // means that we have history path same as current path
        ? backInHistory?.fallback
        : historyOnMount?.pathname || backInHistory?.fallback; // if you copy url where we normally should have history, we should go back fallback

  return title ? (
    <header
      className={cn(
        'box-border flex h-(--header-height) w-full items-center justify-start gap-2 bg-white px-4 pt-2',
        className
      )}
    >
      {href || backInHistory ? (
        <Link
          to={goBackUrl}
          search={backInHistory ? historyOnMount?.search : search}
          className="box-border flex size-7 cursor-pointer items-center justify-center rounded-md border-base-border bg-white hover:border-base-zinc-400"
          replace={!!backInHistory}
        >
          <Icon icon="caret-left" size={11} variant="gray" />
        </Link>
      ) : null}
      <h1 className="overflow-hidden text-md font-medium text-ellipsis whitespace-nowrap">{title}</h1>
      {children}
    </header>
  ) : null;
};
